import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Card, CardBody } from "reactstrap";
import WizardPanel from "../WizardPanel";
import { AsyncButton } from "../../../common/buttons";
import { applyInstallationConfig } from "../../../../services/installationService";
import { captureWizardError } from "../shared/wizardObservability";
import { DvrChannelSection, RecapRow } from "./ChannelSelectionComponents";

/** @param {import("../WizardCard").StepPanelProps} props */
export default function ChannelSelectionPanel({
  installation,
  dvrStore,
  mergeServerFields,
  flushPending,
  setField,
  draftFields,
  status,
  bottomNav,
}) {
  const { dvrs } = dvrStore;
  const persistedDvrs = useMemo(() => (dvrs ?? []).filter((d) => d.id != null), [dvrs]);

  const alreadyApplied =
    installation?.step === "waiting_for_veesion" ||
    installation?.step === "user_creation" ||
    installation?.step === "completed";

  // Selected channels per DVR, reported up from each section.
  const [selectionByDvr, setSelectionByDvr] = useState({});
  const onSelectionChange = useCallback((dvrId, channels) => {
    setSelectionByDvr((prev) => ({ ...prev, [dvrId]: channels }));
  }, []);

  // Drop selections for deleted DVRs.
  const persistedIdsKey = persistedDvrs.map((d) => d.id).join(",");
  useEffect(() => {
    setSelectionByDvr((prev) => {
      const liveIds = new Set(persistedDvrs.map((d) => d.id));
      const next = Object.fromEntries(Object.entries(prev).filter(([id]) => liveIds.has(Number(id))));
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedIdsKey]);

  const [applyError, setApplyError] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const dvrWithoutChannels = persistedDvrs.find((d) => (selectionByDvr[d.id] ?? []).length === 0);

  const handleApply = async () => {
    if (alreadyApplied) return;
    if (persistedDvrs.length === 0) {
      setApplyError("Create at least one DVR before applying.");
      return;
    }
    if (dvrWithoutChannels) {
      setApplyError("Every DVR needs at least one selected channel before applying.");
      return;
    }
    setApplyError(null);
    const payload = {
      dvrs: persistedDvrs.map((d) => ({ dvr_id: d.id, channels: selectionByDvr[d.id] ?? [] })),
    };
    try {
      // Drain in-flight autosaves: a late PATCH response could clobber `step` after apply.
      if (typeof flushPending === "function") {
        await flushPending();
        if (!mountedRef.current) return;
      }
      const { data } = await applyInstallationConfig(installation.uid ?? installation.deal_id, payload);
      if (!mountedRef.current) return;
      if (data?.installation && typeof mergeServerFields === "function") {
        mergeServerFields(data.installation);
      } else {
        setField("step", "waiting_for_veesion");
      }
      dvrStore.reloadDvrs?.();
    } catch (err) {
      captureWizardError(err, "applyConfig", {
        identifier: installation?.uid ?? installation?.deal_id,
        dvrCount: persistedDvrs.length,
      });
      if (!mountedRef.current) return;
      const data = err?.response?.data;
      const detail = data?.detail ?? data?.code ?? err?.message ?? "Failed to apply configuration.";
      setApplyError(detail);
    }
  };

  return (
    <WizardPanel
      title="Channel Selection & Apply"
      subtitle="Pick the channels Veesion will analyze on each DVR, review, then apply the selection"
      status={status}
      bottomNav={bottomNav}
    >
      <Card className="mb-3">
        <CardBody className="py-2">
          <RecapRow label="Store" value={installation?.store_name} />
          <RecapRow label="Location" value={installation?.location} mono />
          <RecapRow label="Install type" value={installation?.is_cloud ? "Cloud" : "Local"} />
        </CardBody>
      </Card>

      {persistedDvrs.length === 0 ? (
        <Alert color="info">Create a DVR on the Video Recorder step before selecting channels.</Alert>
      ) : (
        persistedDvrs.map((dvr, index) => (
          <DvrChannelSection
            key={dvr.id}
            installation={installation}
            dvr={dvr}
            index={index}
            draftFields={draftFields}
            alreadyApplied={alreadyApplied}
            seededChannels={(dvr.channels ?? []).filter((ch) => ch.is_selected)}
            onSelectionChange={onSelectionChange}
          />
        ))
      )}

      <Alert color="info" className="small">
        Applying records the channel selection on the installation; the Store is configured later, once QC validates the
        streams. Applying does <strong>not</strong> push alerts to production yet.
      </Alert>

      <div className="mt-3 d-flex align-items-center" style={{ gap: "0.75rem" }}>
        <AsyncButton color="warning" size="lg" disabled={alreadyApplied} onClick={handleApply}>
          <i className="fas fa-bolt mr-2" />
          {alreadyApplied ? "Configuration applied" : "Apply Configuration"}
        </AsyncButton>
        {applyError && (
          <small className="text-danger">
            <i className="fas fa-times-circle mr-1" />
            {applyError}
          </small>
        )}
        {alreadyApplied && (
          <small className="text-success">
            <i className="fas fa-check-circle mr-1" />
            Channel selection saved.
          </small>
        )}
      </div>
    </WizardPanel>
  );
}
